import React, { useState, useEffect } from "react";
import axios from "axios";
import { HiX } from "react-icons/hi";
import Swal from "sweetalert2";

function CreateEditStaff({ open, onClose, editData, refreshList }) {
  const initialForm = {
    fullName: "",
    phoneNumber: "",
    gender: "",
    rolename: "",
  };

  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [roleList, setRoleList] = useState([]);

  // Fetch roles
  const fetchRoles = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/role-permission/get-all-role`
      );
      setRoleList(res.data.data || []);
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Failed!",
        text: "Error fetching roles",
      });
    }
  };

  useEffect(() => {
    if (open) fetchRoles();

    if (editData) {
      setForm({
        fullName: editData.fullName,
        phoneNumber: editData.phoneNumber,
        gender: editData.gender,
        rolename: editData?.rolePermission?._id || "",
      });
    } else {
      setForm(initialForm);
    }
  }, [editData, open]);

  const handleOutsideClick = (e) => {
    if (e.target.id === "modal-bg") handleClose();
  };

  // Validation
  const validateForm = () => {
    let newErrors = {};

    if (!form.fullName.trim()) newErrors.fullName = "Full Name is required";
    if (!/^\d{10}$/.test(form.phoneNumber))
      newErrors.phoneNumber = "Phone Number must be 10 digits only";
    if (!form.gender.trim()) newErrors.gender = "Please select gender";
    if (!form.rolename.trim()) newErrors.rolename = "Role is required";

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      Swal.fire({
        icon: "warning",
        title: "Validation Error",
        text: "Please fill all required fields correctly.",
      });
    }

    return Object.keys(newErrors).length === 0;
  };

  // Submit
  const handleSubmit = async () => {
    if (!validateForm()) return;

    const payload = {
      fullName: form.fullName,
      phoneNumber: form.phoneNumber,
      gender: form.gender,
      rolePermission: form.rolename,
    };

    try {
      const BASE = process.env.REACT_APP_API_URL;

      const api = editData
        ? `${BASE}/staff/update-staff/${editData._id}`
        : `${BASE}/staff/create-staff`;

      const method = editData ? "put" : "post";

      const res = await axios[method](api, payload);

      Swal.fire({
        icon: "success",
        title: editData ? "Staff Updated!" : "Staff Created!",
        text: res.data.message || "Operation successful.",
        timer: 2000,
      });

      refreshList();
      fetchRoles();
      onClose();
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Failed!",
        text: err.response?.data?.message || "Something went wrong.",
      });
    }
  };

  const handleClose = () => {
    setForm(initialForm);
    setErrors({});
    onClose();
  };

  if (!open) return null;

  return (
    <div
      id="modal-bg"
      onClick={handleOutsideClick}
      className="fixed inset-0 z-50 flex items-center justify-center bg-[#000]/40 backdrop-blur-sm"
    >
      <div className="animate-fadeIn w-full max-w-2xl rounded-2xl bg-white p-6 shadow-2xl">
        {/* HEADER */}
        <div className="flex items-center justify-between border-b pb-3">
          <h2 className="text-xl font-bold text-navy-900">
            {editData ? "Edit Staff" : "Create New Staff"}
          </h2>
          <button onClick={handleClose}>
            <HiX className="h-6 w-6 text-gray-600 hover:text-red-500" />
          </button>
        </div>

        {/* FORM */}
        <div className="mt-4 grid grid-cols-2 gap-4">
          {/* Full Name */}
          <div>
            <input
              className={`w-full rounded-md border px-3 py-2 ${
                errors.fullName && "border-red-500"
              }`}
              placeholder="Full Name"
              value={form.fullName}
              onChange={(e) => setForm({ ...form, fullName: e.target.value })}
            />
          </div>

          {/* Phone Number */}
          <div>
            <input
              className={`w-full rounded-md border px-3 py-2 ${
                errors.phoneNumber && "border-red-500"
              }`}
              maxLength={10}
              placeholder="Phone Number"
              value={form.phoneNumber}
              onChange={(e) =>
                setForm({
                  ...form,
                  phoneNumber: e.target.value.replace(/\D/g, ""),
                })
              }
            />
          </div>

          {/* Gender */}
          <div>
            <select
              className={`w-full rounded-md border px-3 py-2 ${
                errors.gender && "border-red-500"
              }`}
              value={form.gender}
              onChange={(e) => setForm({ ...form, gender: e.target.value })}
            >
              <option value="">Select Gender</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
              <option value="others">Others</option>
            </select>
          </div>

          {/* Role */}
          <div>
            <select
              className={`w-full rounded-md border px-3 py-2 ${
                errors.rolename && "border-red-500"
              }`}
              value={form.rolename}
              onChange={(e) => setForm({ ...form, rolename: e.target.value })}
            >
              <option value="">Select Role</option>
              {roleList.map((role) => (
                <option key={role._id} value={role._id}>
                  {role.rolename}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* FOOTER */}
        <div className="mt-6 flex justify-end gap-3">
          <button
            className="rounded-md bg-gray-300 px-4 py-2 text-gray-800"
            onClick={handleClose}
          >
            Cancel
          </button>
          <button
            className="rounded-md bg-[#c51369] px-4 py-2 text-white"
            onClick={handleSubmit}
          >
            {editData ? "Update Staff" : "Create Staff"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default CreateEditStaff;
